<template>
  <div v-if="hasEye" class="character-card-preview">
    <Tooltip :content="$t('yu-lan')" :placement="'top'">
      <span class="character-card-preview__trigger" @click.stop.prevent="toggleModal">
        <IconPark name="zoom" />
      </span>
    </Tooltip>
    <a-modal
      :open="modalOpen"
      :zIndex="800"
      width="1000px"
      :footer="null"
      :destroyOnClose="true"
      @cancel="toggleModal"
    >
      <template #title>
        <div class="character-card-preview__title">
          <UpgradeBadge
            :config_type="data?.config_type"
            :tab="RIGHTS_TIER_MODAL_TAB.CHARACTER"
            @cancel="toggleModal"
          />
          {{ data.name }}
        </div>
      </template>
      <div class="character-card-preview__modal-content">
        <Carousel class="character-card-preview__carousel">
          <Resource
            v-for="item in list"
            :key="item"
            class="character-card-preview__resource"
            loop
            autoplay
            :videoStartTime="getVideoStartTime(data, item)"
            :src="item"
          ></Resource>
        </Carousel>
        <div class="character-card-preview__actions">
          <div class="character-card-preview__actions-left">
            <TemplateShare
              v-if="data.config?.is_special_outfit !== true"
              iconClass=""
              :title="$t('fen-xiang-3d-shu-zi-ren')"
              :params="{
                share_ids: [data?.id],
                model_type: COLLECTION_MAP.SOURCE,
              }"
              :trackParams="{
                source: resourceLocation?.source,
                id: data?.id,
                tags: data?.tags,
                look_name: data?.look_name,
                name: data?.name,
              }"
              :eventName="TRACK_EVENT.YOUYAN_PLATFORM_SHARE_CHARACTER"
              :shareToInnerUser="false"
              :linkUrl="`${location?.origin}/3d-avatar/${data?.identity}.html`"
              type="shadow"
            >
            </TemplateShare>
            <Collect
              v-if="data"
              class="character-card-preview__collect"
              :model-value="data?.is_star_personal"
              :params="{
                model_type: COLLECTION_MAP.SOURCE,
                model_id: data.id,
                resource_type: RESOURCE_TYPE.PERSON,
                source: COLLECTION_SOURCE.YOUYAN,
              }"
              type="shadow"
              @update:model-value="v => (data!.is_star_personal = v)"
            >
            </Collect>
          </div>
          <div
            v-if="data.config?.is_special_outfit === true"
            class="character-card-preview__actions-right"
          >
            <a-button
              type="primary"
              class="character-card-preview__button character-card-preview__button--special"
              :disabled="true"
            >
              <iconpark-icon
                name="ai"
                size="16"
                class="character-card-preview__button-icon"
              ></iconpark-icon>
              {{ $t('ren-wu-jin-zhan-shi') }}
            </a-button>
          </div>
          <div v-else class="character-card-preview__actions-right">
            <UpgradeContent
              :config_type="data.config_type"
              @cancel="toggleModal"
              class="character-card-preview__button character-card-preview__button--create"
            >
              <a-button
                class="character-card-preview__button character-card-preview__button--create"
                @click.stop="$emit('createOps', 'person')"
              >
                <AiColorfulSvg class="character-card-preview__button-icon" />
                {{ $t('yong-ta-chuang-zuo') }}
              </a-button>
            </UpgradeContent>
            <a-button
              v-if="
                hasActionPermission(GENERIC_ACTION_KEYS.AVATAR_EDIT) &&
                !is2DCharacter(props.data.config?.xhuman_type)
              "
              type="primary"
              class="character-card-preview__button character-card-preview__button--edit"
              @click.stop="$emit('createOps', 'image')"
            >
              <LimitedTimeTag
                :user="userInfo"
                :config_type="data.config_type"
                @cancel="toggleModal"
              />
              <AiSvg class="character-card-preview__button-icon" />
              {{ $t('bian-ji-ren-wu') }}
            </a-button>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>
<script lang="ts" setup>
import { GENERIC_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import LimitedTimeTag from '@rendora/common/features/limited-time-tag/index.vue';
import UpgradeBadge from '@rendora/common/features/upgrade-badge/index.vue';
import { RIGHTS_TIER_MODAL_TAB } from '@rendora/common/types/enums';
import { is2DCharacter } from '@rendora/common/utils/is';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { useI18n } from 'vue-i18n';
import type { Character } from '~/api/interface/character';
import AiColorfulSvg from '~/assets/svg/ai-colorful.svg?component';
import AiSvg from '~/assets/svg/ai.svg?component';
import UpgradeContent from '~/components/upgrade-content/index.vue';
import { usePageAndActionPermissionData } from '~/composables/permission';
import Collect from '~/features/collect/index.vue';
import { TemplateShare } from '~/features/template';
import { COLLECTION_MAP, COLLECTION_SOURCE, RESOURCE_TYPE } from '~/utils/constant';
import { TRACK_EVENT } from '~/utils/track';

const { t: $t } = useI18n();

interface CharacterCardProps {
  data: Character;
}

const props = defineProps<CharacterCardProps>();

const userInfo = useUserInfo();

const { hasActionPermission } = usePageAndActionPermissionData();

const emit = defineEmits({
  createOps: (v: 'image' | 'person') => true,
});

// 判断是否存在预览数据
const resourceLocation = injectResourceLocation();

const hasEye = computed(() => !!props.data.preview_videos && props.data.preview_videos.length > 0);

const list = computed(() => props.data?.preview_videos);

const modalOpen = ref(false);

function toggleModal() {
  modalOpen.value = !modalOpen.value;
}

const location = computed(() => {
  if (import.meta.client) {
    return window.location;
  }

  return null;
});

function getVideoStartTime(avatar: Character, src?: string) {
  if (src?.endsWith('new_face.mp4')) {
    return avatar.config?.gender === 'Male' ? 3 : 11;
  }
  return 0;
}
</script>

<style lang="scss" scoped>
// Element: trigger
.character-card-preview__trigger {
  width: 28px;
  height: 28px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }
}

.character-card-preview__title {
  display: flex;
  align-items: center;
  gap: 4px;
}

// Element: carousel
.character-card-preview__carousel {
  border-radius: 12px;
  overflow: hidden;
}

// Element: resource
.character-card-preview__resource {
  width: 944px;
  object-fit: contain;
  background-color: var(--x-back-1);
  height: 535px;
}

// Element: actions
.character-card-preview__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

// Element: actions-left
.character-card-preview__actions-left {
  display: flex;
  align-items: center;
}

// Element: collect
.character-card-preview__collect {
  margin-left: 4px;
}

// Element: actions-right
.character-card-preview__actions-right {
  display: flex;
  align-items: center;
}

// Element: button
.character-card-preview__button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  font-weight: 500;
}

// Modifier: button--special
.character-card-preview__button--special {
  min-width: 216px;
  border-radius: 8px;
}

// Modifier: button--edit
.character-card-preview__button--edit {
  min-width: 136px;
}

// Modifier: button--create
.character-card-preview__button--create {
  // min-width: 216px;
  min-width: 136px;
  margin-right: 12px;
}

// Element: button-icon
.character-card-preview__button-icon {
  font-size: 16px;
  margin-right: 4px;
}
</style>
